import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Download,
  Headset,
  Plus,
  Search,
  Wrench,
  X,
} from "lucide-react";

import { ApiError, formatDateTime } from "../../lib/api";
import { getAuth, hasPermission } from "../../lib/auth";
import { useToast } from "../../components/Toast";
import {
  Badge,
  type BadgeTone,
  Button,
  Card,
  Cargando,
  ErrorCarga,
  PageHeader,
  TableWrap,
} from "../../components/ui";
import {
  CANAL_LABELS,
  ESTADO_LABELS,
  TIPO_LABELS,
  descargarCsv,
  fetchAtenciones,
  folioAtencion,
  type Atencion,
  type AtencionFilters,
  type CanalRecepcion,
  type EstadoAtencion,
  type TipoAtencion,
} from "./api";
import { CrearAtencionModal } from "./CrearAtencionModal";
import { ResponderAtencionModal } from "./ResponderAtencionModal";
import { DerivarOtModal } from "./DerivarOtModal";
import { fechaDMA, hoyLocalISO, parseFechaLocal } from "../../lib/fechaLocal";

const PAGE_SIZE = 50;

const TIPOS: TipoAtencion[] = ["Solicitud", "Consulta", "Emergencia", "Reclamo"];
const ESTADOS: EstadoAtencion[] = ["EnProceso", "Resuelto", "Denegado"];
const CANALES: CanalRecepcion[] = [
  "presencial",
  "telefono",
  "email",
  "whatsapp",
  "carta",
  "libro",
];

export function AtencionesListPage(): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();

  // Filtros iniciales desde la URL: los links del Inicio llegan ya filtrados
  // (?estado=EnProceso, ?fuera_de_plazo=1, ?tipo=Reclamo).
  const [params] = useSearchParams();
  const estadoUrl = params.get("estado");
  const tipoUrl = params.get("tipo");
  const [tipo, setTipo] = useState<TipoAtencion | "">(
    TIPOS.includes(tipoUrl as TipoAtencion) ? (tipoUrl as TipoAtencion) : "",
  );
  const [estado, setEstado] = useState<EstadoAtencion | "">(
    ESTADOS.includes(estadoUrl as EstadoAtencion) ? (estadoUrl as EstadoAtencion) : "",
  );
  const [plazo, setPlazo] = useState<"" | "fuera" | "por_vencer">(
    params.get("fuera_de_plazo") === "1"
      ? "fuera"
      : params.get("por_vencer") === "1"
        ? "por_vencer"
        : "",
  );
  const [canal, setCanal] = useState<CanalRecepcion | "">("");
  const [desde, setDesde] = useState<string>("");
  const [hasta, setHasta] = useState<string>("");
  const [q, setQ] = useState<string>("");
  const [page, setPage] = useState<number>(0);

  const [crearAbierto, setCrearAbierto] = useState(false);
  const [responder, setResponder] = useState<Atencion | null>(null);

  const offset = page * PAGE_SIZE;

  const filtros: AtencionFilters = useMemo(
    () => ({
      tipo: tipo || null,
      estado: estado || null,
      canal: canal || null,
      desde: desde || null,
      hasta: hasta || null,
      q: q.trim() || null,
      fuera_de_plazo: plazo === "fuera" || null,
      por_vencer: plazo === "por_vencer" || null,
      limit: PAGE_SIZE,
      offset,
    }),
    [tipo, estado, canal, desde, hasta, q, plazo, offset],
  );

  const listQ = useQuery({
    queryKey: ["atenciones", filtros],
    queryFn: () => fetchAtenciones(filtros),
  });

  const exportarMut = useMutation({
    mutationFn: () =>
      descargarCsv({
        tipo: tipo || null,
        estado: estado || null,
        canal: canal || null,
        desde: desde || null,
        hasta: hasta || null,
        q: q.trim() || null,
      }),
    onSuccess: () => toast.success("CSV descargado"),
    onError: (err: unknown) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo exportar";
      toast.error(msg);
    },
  });

  const total = listQ.data?.total ?? 0;
  const items = listQ.data?.items ?? [];
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function resetFiltros(): void {
    setTipo("");
    setEstado("");
    setCanal("");
    setDesde("");
    setHasta("");
    setQ("");
    setPlazo("");
    setPage(0);
  }

  // El modal queda abierto mostrando el comprobante; sólo refresca la lista.
  function onCreated(): void {
    void qc.invalidateQueries({ queryKey: ["atenciones"] });
  }

  function onResponded(): void {
    setResponder(null);
    qc.invalidateQueries({ queryKey: ["atenciones"] });
    toast.success("Atención cerrada");
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Atenciones"
        icon={Headset}
        subtitle={
          <>
            Cada consulta, solicitud o reclamo que llega al comité, por cualquier
            canal. Hay que responder en 20 días hábiles; las emergencias, el mismo
            día.
            {listQ.data && (
              <span className="ml-1 tabular-nums">
                ({total.toLocaleString("es-CL")} registros)
              </span>
            )}
          </>
        }
        actions={
        <>
          <Button
            variant="secondary"
            icon={Download}
            onClick={() => exportarMut.mutate()}
            disabled={exportarMut.isPending}
            title="Planilla con las atenciones filtradas, en el formato que pide la SISS"
          >
            {exportarMut.isPending ? "Generando…" : "Exportar planilla SISS"}
          </Button>
          <Button icon={Plus} onClick={() => setCrearAbierto(true)}>
            Nueva atención
          </Button>
        </>
        }
      />

      {/* Filtros */}
      <Card padding="sm">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <Field label="Buscar">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-500" strokeWidth={1.5} />
              <input
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setPage(0);
                }}
                placeholder="Nombre / RUT / motivo"
                className="w-full rounded-md border border-neutral-450 pl-7 pr-2 py-1.5 text-sm shadow-sm"
              />
            </div>
          </Field>
          <Field label="Tipo">
            <select
              value={tipo}
              onChange={(e) => {
                setTipo(e.target.value as TipoAtencion | "");
                setPage(0);
              }}
              className="w-full rounded-md border border-neutral-450 px-2 py-1.5 text-sm shadow-sm"
            >
              <option value="">Todos</option>
              {TIPOS.map((t) => (
                <option key={t} value={t}>
                  {TIPO_LABELS[t]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Estado">
            <select
              value={estado}
              onChange={(e) => {
                setEstado(e.target.value as EstadoAtencion | "");
                setPage(0);
              }}
              className="w-full rounded-md border border-neutral-450 px-2 py-1.5 text-sm shadow-sm"
            >
              <option value="">Todos</option>
              {ESTADOS.map((e) => (
                <option key={e} value={e}>
                  {ESTADO_LABELS[e]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Canal">
            <select
              value={canal}
              onChange={(e) => {
                setCanal(e.target.value as CanalRecepcion | "");
                setPage(0);
              }}
              className="w-full rounded-md border border-neutral-450 px-2 py-1.5 text-sm shadow-sm"
            >
              <option value="">Todos</option>
              {CANALES.map((c) => (
                <option key={c} value={c}>
                  {CANAL_LABELS[c]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Desde">
            <input
              type="date"
              value={desde}
              onChange={(e) => {
                setDesde(e.target.value);
                setPage(0);
              }}
              className="w-full rounded-md border border-neutral-450 px-2 py-1.5 text-sm shadow-sm"
            />
          </Field>
          <Field label="Hasta">
            <input
              type="date"
              value={hasta}
              onChange={(e) => {
                setHasta(e.target.value);
                setPage(0);
              }}
              className="w-full rounded-md border border-neutral-450 px-2 py-1.5 text-sm shadow-sm"
            />
          </Field>
        </div>
        <div className="mt-2 flex items-center justify-between gap-3">
          <label className="flex items-center gap-2 text-sm text-neutral-700">
            Plazo
            <select
              value={plazo}
              onChange={(e) => {
                setPlazo(e.target.value as "" | "fuera" | "por_vencer");
                setPage(0);
              }}
              className="rounded-md border border-neutral-450 px-2 py-1 text-sm shadow-sm"
            >
              <option value="">Todos</option>
              <option value="fuera">Fuera de plazo</option>
              <option value="por_vencer">Vencen en 3 días o menos</option>
            </select>
          </label>
          <Button variant="link" size="sm" icon={X} onClick={resetFiltros}>
            Limpiar filtros
          </Button>
        </div>
      </Card>

      {listQ.isPending && <Cargando texto="Cargando atenciones…" />}
      {listQ.isError && (
        <ErrorCarga
          error={listQ.error}
          mensaje="Error al cargar atenciones."
          onReintentar={() => void listQ.refetch()}
        />
      )}

      {listQ.data && items.length === 0 && (
        <p className="text-sm text-neutral-600">
          Sin atenciones para los filtros aplicados.
        </p>
      )}

      {items.length > 0 && (
        <TableWrap label="Atenciones">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-left">N°</th>
                <th scope="col" className="px-3 py-2 text-left">Ingreso</th>
                <th scope="col" className="px-3 py-2 text-left">Tipo</th>
                <th scope="col" className="px-3 py-2 text-left">Usuario</th>
                <th scope="col" className="px-3 py-2 text-left">Canal</th>
                <th scope="col" className="px-3 py-2 text-left">Plazo de respuesta</th>
                <th scope="col" className="px-3 py-2 text-left">Estado</th>
                <th scope="col" className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {items.map((a) => (
                <AtencionRow
                  key={a.id}
                  atencion={a}
                  onResponder={() => setResponder(a)}
                />
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-neutral-600">
          <span>
            Página {page + 1} de {totalPages} · {total.toLocaleString("es-CL")}{" "}
            registros
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              Anterior
            </Button>
            <Button
              variant="secondary"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}

      {crearAbierto && (
        <CrearAtencionModal
          onClose={() => setCrearAbierto(false)}
          onCreated={onCreated}
        />
      )}
      {responder && (
        <ResponderAtencionModal
          atencion={responder}
          onClose={() => setResponder(null)}
          onResponded={onResponded}
        />
      )}
    </div>
  );
}

function AtencionRow({
  atencion,
  onResponder,
}: {
  atencion: Atencion;
  onResponder: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [derivando, setDerivando] = useState(false);
  const enProceso = atencion.estado === "EnProceso";
  const puedeDerivar = hasPermission(getAuth(), "mantenimiento.gestionar");

  return (
    <>
      <tr className={open ? "bg-neutral-50" : "hover:bg-neutral-50"}>
        <td className="px-3 py-2 whitespace-nowrap tabular-nums text-neutral-700">
          {folioAtencion(atencion)}
        </td>
        <td className="px-3 py-2 whitespace-nowrap text-neutral-700">
          {formatDateTime(atencion.fecha_ingreso)}
        </td>
        <td className="px-3 py-2">
          <TipoBadge tipo={atencion.tipo} />
        </td>
        <td className="px-3 py-2 text-neutral-700">
          <div>{atencion.usuario_nombre}</div>
          {atencion.usuario_rut && (
            <div className="text-xs text-neutral-500">{atencion.usuario_rut}</div>
          )}
        </td>
        <td className="px-3 py-2 text-neutral-600">
          {CANAL_LABELS[atencion.canal_recepcion]}
        </td>
        <td className="px-3 py-2 text-neutral-600">
          <PlazoBadge atencion={atencion} />
        </td>
        <td className="px-3 py-2">
          <EstadoBadge estado={atencion.estado} />
          {atencion.ot_numero && (
            <div className="mt-1 whitespace-nowrap text-xs tabular-nums text-neutral-600">
              OT {atencion.ot_numero}
            </div>
          )}
        </td>
        <td className="px-3 py-2 text-right">
          <div className="flex justify-end gap-2">
            <Button variant="link" size="sm" onClick={() => setOpen((v) => !v)}>
              {open ? "Ocultar" : "Detalle"}
            </Button>
            {enProceso && (
              <Button size="sm" onClick={onResponder}>
                Responder
              </Button>
            )}
          </div>
        </td>
      </tr>
      {open && (
        <tr className="bg-neutral-50/60">
          <td colSpan={8} className="px-4 py-3 text-sm">
            <DetalleAtencion atencion={atencion} />
            {atencion.ot_id != null && atencion.ot_numero && (
              <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-neutral-700">
                <Wrench className="h-4 w-4 text-neutral-500" strokeWidth={1.5} aria-hidden />
                <span>
                  Derivada a OT <span className="tabular-nums">{atencion.ot_numero}</span>
                </span>
                <Link
                  to={`/ordenes-trabajo?ot=${atencion.ot_id}`}
                  className="font-medium text-brand-700 underline hover:text-brand-800"
                >
                  Ver OT
                </Link>
              </div>
            )}
            {enProceso && puedeDerivar && atencion.ot_id == null && (
              <div className="mt-3">
                <Button
                  variant="secondary"
                  size="sm"
                  icon={Wrench}
                  onClick={() => setDerivando(true)}
                >
                  Derivar a OT
                </Button>
              </div>
            )}
            {derivando && (
              <DerivarOtModal atencion={atencion} onClose={() => setDerivando(false)} />
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function DetalleAtencion({ atencion }: { atencion: Atencion }): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
          Descripción
        </div>
        <p className="mt-1 whitespace-pre-wrap text-neutral-700">
          {atencion.descripcion}
        </p>
      </div>
      <div className="space-y-1 text-neutral-700">
        {atencion.usuario_telefono && (
          <div>
            <span className="text-xs text-neutral-500">Teléfono: </span>
            {atencion.usuario_telefono}
          </div>
        )}
        {atencion.usuario_email && (
          <div>
            <span className="text-xs text-neutral-500">Correo: </span>
            {atencion.usuario_email}
          </div>
        )}
        {atencion.contrato_id && (
          <div>
            <span className="text-xs text-neutral-500">Contrato: </span>
            {atencion.contrato_id}
          </div>
        )}
        {atencion.fecha_limite_respuesta && (
          <div>
            <span className="text-xs text-neutral-500">Plazo de respuesta: </span>
            {fechaDMA(atencion.fecha_limite_respuesta)}
          </div>
        )}
      </div>
      {atencion.fecha_respuesta && (
        <div className="md:col-span-2">
          <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
            Respuesta — {formatDateTime(atencion.fecha_respuesta)}
          </div>
          <p className="mt-1 whitespace-pre-wrap text-neutral-700">
            {atencion.tipo_solucion}
          </p>
        </div>
      )}
      {atencion.observaciones && (
        <div className="md:col-span-2">
          <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
            Observaciones
          </div>
          <p className="mt-1 whitespace-pre-wrap text-neutral-700">
            {atencion.observaciones}
          </p>
        </div>
      )}
    </div>
  );
}

// Mapa tipo → tono (DESIGN §2.4); Solicitud va en `brand`.
const TIPO_TONE: Record<TipoAtencion, BadgeTone> = {
  Solicitud: "brand",
  Consulta: "slate",
  Emergencia: "rose",
  Reclamo: "amber",
};

function TipoBadge({ tipo }: { tipo: TipoAtencion }): JSX.Element {
  return (
    <Badge tone={TIPO_TONE[tipo]}>
      {tipo === "Emergencia" && <AlertTriangle className="h-3 w-3" strokeWidth={1.5} />}
      {TIPO_LABELS[tipo]}
    </Badge>
  );
}

function EstadoBadge({ estado }: { estado: EstadoAtencion }): JSX.Element {
  if (estado === "Resuelto") {
    return (
      <Badge tone="emerald">
        <CheckCircle2 className="h-3 w-3" strokeWidth={1.5} />
        Resuelto
      </Badge>
    );
  }
  if (estado === "Denegado") {
    return <Badge tone="slate">Denegado</Badge>;
  }
  return (
    <Badge tone="amber">
      <Clock className="h-3 w-3" strokeWidth={1.5} />
      En proceso
    </Badge>
  );
}

function PlazoBadge({ atencion }: { atencion: Atencion }): JSX.Element {
  if (!atencion.fecha_limite_respuesta) return <span className="text-neutral-500">—</span>;

  // La fecha límite es un día de calendario: se compara en hora local
  // (con `new Date("YYYY-MM-DD")` en Chile caía el día anterior).
  const limite = parseFechaLocal(atencion.fecha_limite_respuesta);
  const cerrada = atencion.estado !== "EnProceso";
  if (cerrada) {
    return (
      <span className="text-xs text-neutral-600 tabular-nums">
        {fechaDMA(atencion.fecha_limite_respuesta)}
      </span>
    );
  }

  const hoyMid = parseFechaLocal(hoyLocalISO());
  const diff = Math.round(
    (limite.getTime() - hoyMid.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (diff < 0) {
    return <Badge tone="rose">Vencido hace {Math.abs(diff)} d</Badge>;
  }
  if (diff === 0) {
    return <Badge tone="amber">Vence hoy</Badge>;
  }
  if (diff <= 3) {
    return <Badge tone="amber">Vence en {diff} d</Badge>;
  }
  return (
    <span className="text-xs text-neutral-600">
      Vence en {diff} d ({fechaDMA(atencion.fecha_limite_respuesta)})
    </span>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-neutral-600">
        {label}
      </label>
      {children}
    </div>
  );
}
